import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, ErrorState, Input, MoneyInput, PageHeader, PageLoader, Select, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { BillUpload } from '../../../components/common/BillUpload/BillUpload';
import { JobPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { PAYMENT_MODE_OPTIONS } from '../../../constants/payments';
import { masterLabel, masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { expenseService } from '../services/expenseService';
import { jobService } from '../../jobs/services/jobService';
import type { PaymentMode } from '../../../types/common';
import type { FormErrors } from '../../../hooks/useForm';

const EMPTY = {
  date: toISODate(new Date()), category: 'fuel', amount: null, gstAmount: null, vendorName: '', vendorGstin: '', billNo: '',
  billPhotoUrl: '', serviceId: null as string | null, serviceLabel: '', paymentMode: 'cash' as PaymentMode, notes: '',
};


export default function ExpenseForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  useMasters();
  const [saving, setSaving] = useState(false);
  const { data: exp, loading, error, reload } = useFetch(
    () => (editing ? expenseService.get(id) : Promise.resolve(null)),
    [id],
  );
  useCrumb(`/expenses/${id}`, exp?.expenseNo);

  useEffect(() => {
    if (exp) {
      setForm({
        ...EMPTY, date: toISODate(new Date(exp.date)), category: exp.category, amount: exp.amount, gstAmount: exp.gstAmount || null,
        vendorName: exp.vendorName ?? '', vendorGstin: exp.vendorGstin ?? '', billNo: exp.billNo ?? '', billPhotoUrl: exp.billPhotoUrl ?? '',
        serviceId: exp.serviceId ?? null, paymentMode: exp.paymentMode, notes: exp.notes ?? '',
      });
      if (exp.serviceId) {
        jobService.get(exp.serviceId).then((j) => setForm((f) => ({ ...f, serviceLabel: `${j.serviceNo} · ${j.customer?.name}` }))).catch(() => {});
      }
    }
  }, [exp, setForm]);
  useEffect(() => {
    const sid = params.get('serviceId');
    if (!editing && sid) jobService.get(sid).then((j) => setForm((f) => ({ ...f, serviceId: sid, serviceLabel: `${j.serviceNo} · ${j.customer?.name}` }))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!editing && !(form.amount > 0)) errs.amount = 'Enter the amount paid';
    if ((form.gstAmount ?? 0) > (form.amount ?? 0)) errs.gstAmount = 'GST cannot be more than the amount';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const details = {
      category: form.category,
      vendorName: trimOrUndef(form.vendorName),
      vendorGstin: trimOrUndef(form.vendorGstin.toUpperCase()),
      billNo: trimOrUndef(form.billNo),
      billPhotoUrl: form.billPhotoUrl || undefined,
      notes: trimOrUndef(form.notes),
    };
    setSaving(true);
    try {
      // Amount, date and mode are fixed once saved (the payment is already recorded).
      const data = id
        ? await expenseService.update(id, { ...details, serviceId: form.serviceId || null })
        : await expenseService.create({ ...details, serviceId: form.serviceId || undefined, date: form.date || undefined, amount: form.amount, gstAmount: form.gstAmount ?? 0, paymentMode: form.paymentMode });
      toast.success(editing ? 'Expense updated' : `Expense ${data.expenseNo} saved`);
      navigate(`/expenses/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !exp) return <PageLoader />;
  if (exp && exp.status !== 'active') return <ErrorState error={{ message: 'A cancelled expense cannot be edited.' }} />;

  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(editing ? `/expenses/${id}` : '/expenses')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="expense-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Save expense'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${exp?.expenseNo ?? 'expense'}` : 'Add expense'} subtitle={editing ? 'Amount, date and mode cannot change. Cancel and add again to fix them.' : 'Saving also records the money going out.'} actions={actions} />
      <form id="expense-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Expense">
                <Select label="Category" options={masterOptions('expenseCategory', { keep: form.category })} value={form.category} onChange={set('category')} />
                <DatePicker label="Date" value={form.date} onChange={set('date')} clearable={false} disabled={editing} max={toISODate(new Date())} />
                <MoneyInput label="Amount paid" required value={form.amount} onChange={set('amount')} error={errors.amount} disabled={editing} placeholder="0" hint="Total including GST" />
                <MoneyInput label="GST in this amount" value={form.gstAmount} onChange={set('gstAmount')} error={errors.gstAmount} disabled={editing} placeholder="0" hint="Only if the bill shows GST" />
                <Select label="Paid by" options={PAYMENT_MODE_OPTIONS} value={form.paymentMode} onChange={set('paymentMode')} disabled={editing} searchable={false} />
                <JobPicker label="For a job (optional)" value={form.serviceId} selectedLabel={form.serviceLabel} onChange={(sid, s) => setForm((f) => ({ ...f, serviceId: sid, serviceLabel: s ? `${s.serviceNo} · ${s.customer?.name}` : '' }))} hint="Counts in that job's profit" />
              </FormSection>
              <FormSection title="Bill">
                <Input label="Paid to" value={form.vendorName} onChange={set('vendorName')} placeholder="Shop, petrol pump, person…" />
                <Input label="Bill number" value={form.billNo} onChange={set('billNo')} placeholder="Optional" />
                <Input label="Their GSTIN" value={form.vendorGstin} onChange={(e) => set('vendorGstin')(e.target.value.toUpperCase())} maxLength={15} placeholder="Optional" />
                <BillUpload value={form.billPhotoUrl} onChange={set('billPhotoUrl')} />
                <Textarea label="Notes" rows={2} value={form.notes} onChange={set('notes')} maxLength={1000} containerClassName="sm:col-span-2" />
              </FormSection>
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Summary" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Category', masterLabel('expenseCategory', form.category)],
                    ['Before GST', money((form.amount ?? 0) - (form.gstAmount ?? 0))],
                    ['GST', money(form.gstAmount)],
                    ['Total paid', money(form.amount), { strong: true }],
                  ]}
                />
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
